import React, { useState } from 'react';
import { clientsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { UserPlus, Loader2 } from 'lucide-react';

/**
 * Pulsante + mini-form per creare un cliente al volo.
 * Props:
 *   onCreated(client) — chiamato dopo la creazione con il nuovo cliente
 */
export const QuickClientDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nome: '', email: '', telefono: '', azienda: '' });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === 'nome') setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { setError('Il nome è obbligatorio'); return; }
    setSaving(true);
    try {
      const res = await clientsAPI.create({
        nome: form.nome.trim(),
        email: form.email.trim() || undefined,
        telefono: form.telefono.trim() || undefined,
        azienda: form.azienda.trim() || undefined,
      });
      toast.success(`Cliente "${res.data.nome}" creato`);
      onCreated(res.data);
      setOpen(false);
      setForm({ nome: '', email: '', telefono: '', azienda: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione del cliente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="shrink-0"
          title="Nuovo cliente al volo"
        >
          <UserPlus className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Nuovo Cliente</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-1">
          <div className="space-y-1">
            <Label htmlFor="qc-nome">Nome / Ragione sociale *</Label>
            <Input
              id="qc-nome"
              name="nome"
              value={form.nome}
              onChange={handleChange}
              autoFocus
              placeholder="Es. Mario Rossi"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="qc-azienda">Azienda</Label>
            <Input
              id="qc-azienda"
              name="azienda"
              value={form.azienda}
              onChange={handleChange}
              placeholder="Es. Acme Srl"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="qc-email">Email</Label>
              <Input
                id="qc-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="email@esempio.it"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="qc-telefono">Telefono</Label>
              <Input
                id="qc-telefono"
                name="telefono"
                value={form.telefono}
                onChange={handleChange}
                placeholder="+39 000 000 0000"
              />
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Puoi completare gli altri dati dalla sezione Clienti.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crea cliente'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
